/**
 * Record Payment modal (design "Modals") — amount/date/mode/reference, a TDS
 * section (toggle → amount + section + plain-language explanation), per-open-
 * document allocation rows with Auto-allocate, and a live footer summary
 * (settles / allocated / TDS receivable / unallocated→advance).
 */
import React, { useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import ModalShell from './modalShell';
import { useTheme } from '../../context/themeContext';
import SkIcon from '../skIcon';
import DropdownSelect from '../dropdownSelect';
import DateField from '../dateField';
import { DocRow, fmtMoney, money, DEFAULT_CURRENCY } from '../../utils/format';
import { listDocuments } from '../../services/billing/document.service';
import { OpenDoc, openDocsFor, spreadAllocation } from '../../utils/paymentAllocation';
import { createPayment } from '../../services/billing/payment.service';
import { PaymentMode } from '../../schema/billing/common';
import { WithholdingEntry } from '../../schema/billing/payment';

const web = (s: Record<string, unknown>): any => (Platform.OS === 'web' ? s : {});

interface Props {
  visible: boolean;
  onClose: () => void;
  doc: DocRow | null;
  /** CRM contact id of the document's party (DocRow only carries the name). */
  contactId?: string;
  /** Document currency (3-letter ISO); defaults to INR. */
  currency?: string;
  /** Called after the payment persists successfully (parent closes + refreshes). */
  onSaved: () => void;
}


// [enum value, display label] — the API expects the enum, the user sees the label.
const MODES: [PaymentMode, string][] = [
  ['BANK_TRANSFER', 'Bank transfer'],
  ['RAZORPAY', 'Razorpay'],
  ['CHEQUE', 'Cheque'],
  ['CASH', 'Cash'],
  ['OTHER', 'Other'],
];
const SECTIONS = ['194J · Professional fees', '194C · Contractors', '194H · Commission'];
const PURCHASE_TYPES = ['BILL', 'VENDOR_CREDIT', 'EXPENSE'];

const RecordPaymentModal: React.FC<Props> = ({ visible, onClose, doc, contactId, currency, onSaved }) => {
  // The document's own currency; the modal must never assert one of its own.
  const cur = currency || DEFAULT_CURRENCY;
  const { theme } = useTheme();
  const { colors, font } = theme;

  const contact = doc?.customer || '';
  // Purchase documents (bills/expenses) are money paid OUT; everything else is received.
  const isPurchase = doc ? PURCHASE_TYPES.includes(doc.type) : false;
  const [opens, setOpens] = useState<OpenDoc[]>([]);
  const [loading, setLoading] = useState(false);

  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [mode, setMode] = useState<PaymentMode>('BANK_TRANSFER');
  const [ref, setRef] = useState('');
  const [tdsOn, setTdsOn] = useState(false);
  const [tdsAmount, setTdsAmount] = useState('0');
  const [section, setSection] = useState(SECTIONS[0]);
  const [allocs, setAllocs] = useState<Record<string, string>>({});
  // Allocations follow the amount until the user types one by hand.
  const [manualAlloc, setManualAlloc] = useState(false);
  const [busy, setBusy] = useState(false);

  // Every open starts clean. The modal stays mounted between payments, so
  // without this the second payment inherited the first one's reference and
  // allocations, and a "Saving…" button that never re-enabled.
  useEffect(() => {
    if (!visible) return;
    setAmount('');
    setDate('');
    setMode('BANK_TRANSFER');
    setRef('');
    setTdsOn(false);
    setTdsAmount('0');
    setSection(SECTIONS[0]);
    setAllocs({});
    setManualAlloc(false);
    setBusy(false);
  }, [visible]);

  // This party's open documents of the matching side, THIS document first.
  // Matched on the contact id — two customers can share a name.
  useEffect(() => {
    if (!visible || !doc || !contactId) { setOpens([]); return; }
    let alive = true;
    setLoading(true);
    const types = isPurchase ? (['BILL', 'EXPENSE'] as const) : (['INVOICE'] as const);
    Promise.all(types.map((type) => listDocuments({ type })))
      .then((lists) => {
        if (!alive) return;
        const rows = openDocsFor(lists.flat(), contactId, doc.id);
        setOpens(rows);
        // Default: pay this document's balance, allocated to it.
        const own = rows.find((o) => o.id === doc.id);
        if (own) setAmount(own.balance.toFixed(2));
      })
      .catch(() => { if (alive) setOpens([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [visible, doc, contactId, isPurchase]);

  const amt = parseFloat(amount) || 0;
  const tds = tdsOn ? parseFloat(tdsAmount) || 0 : 0;
  const gross = amt + tds;

  /** Spread over the open documents in list order (this document first). */
  const spread = (avail: number) => spreadAllocation(opens, avail);
  const autoAllocate = () => { setManualAlloc(false); setAllocs(spread(gross)); };
  useEffect(() => {
    if (!manualAlloc) setAllocs(spread(gross));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gross, opens, manualAlloc]);

  const allocated = opens.reduce((s, o) => s + (parseFloat(allocs[o.id]) || 0), 0);
  const unalloc = gross - allocated;

  // contact_id isn't on DocRow — it comes from the loaded document.
  const resolvedContactId = contactId || '';
  // Sent to the server on the payment, so it follows the document being
  // settled rather than a constant — a payment against an AED invoice must
  // not be recorded in rupees.
  const resolvedCurrency = currency || opens.find((o) => o.currency)?.currency || DEFAULT_CURRENCY;
  const canSave = !busy && amt > 0 && !!resolvedContactId && unalloc > -0.005;

  const save = async () => {
    if (!canSave) return;
    const allocations = opens
      .map((o) => ({ document_id: o.id, amount: parseFloat(allocs[o.id]) || 0 }))
      .filter((a) => a.amount > 0)
      .map((a) => ({ document_id: a.document_id, amount: a.amount.toFixed(2) }));
    const withholding: WithholdingEntry[] =
      tdsOn && tds > 0
        ? [{ kind: isPurchase ? 'TDS_ON_VENDOR' : 'TDS_BY_CUSTOMER', section_code: section.split(' ')[0], amount: tds.toFixed(2) }]
        : [];
    setBusy(true);
    try {
      await createPayment({
        direction: isPurchase ? 'MADE' : 'RECEIVED',
        contact_id: resolvedContactId,
        payment_date: date || new Date().toISOString().slice(0, 10),
        mode,
        currency: resolvedCurrency,
        amount: amt.toFixed(2),
        reference: ref.trim() || undefined,
        allocations,
        withholding,
      });
      onSaved();
    } catch {
      // error toast shown by the service layer; keep the modal open to retry
    } finally {
      setBusy(false);
    }
  };

  const inp = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 9, height: 40, paddingHorizontal: 12, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...web({ outlineStyle: 'none' }) };
  const fieldLabel = { fontFamily: font.sansMedium, fontSize: 12, color: colors.ink3, marginBottom: 6, fontWeight: '500' as const };
  const symbol = cur === 'INR' ? '₹' : cur;
  const advColor = unalloc > 0 ? colors.warn : unalloc < 0 ? colors.danger : colors.success;
  const advLabel = unalloc > 0 ? 'Unallocated → advance' : unalloc < 0 ? 'Over-allocated' : 'Fully allocated';

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title={isPurchase ? 'Record payment made' : 'Record payment received'}
      subtitle={contact ? `${isPurchase ? 'to' : 'from'} ${contact}` : undefined}
      width={660}
      headerTone="accent"
      footer={
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}>
            <Summary label="Settles" value={fmtMoney(gross, cur)} color={colors.ink} font={font} colors={colors} />
            <Summary label="Allocated" value={fmtMoney(allocated, cur)} color={colors.ink} font={font} colors={colors} />
            <Summary label={isPurchase ? "TDS payable" : "TDS recv."} value={tds > 0 ? fmtMoney(tds, cur) : '—'} color={tds > 0 ? colors.info : colors.ink4} font={font} colors={colors} />
            <Summary label={advLabel} value={fmtMoney(Math.abs(unalloc), cur)} color={advColor} font={font} colors={colors} />
          </View>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
              <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Cancel</Text>
            </Pressable>
            <Pressable onPress={save} disabled={!canSave} style={{ height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: canSave ? colors.success : colors.paper3, ...web(canSave ? { backgroundImage: `linear-gradient(145deg, ${colors.success}, ${colors.successLine})`, boxShadow: `0 6px 14px -4px ${colors.success}88`, cursor: 'pointer' } : { cursor: 'not-allowed' }) }}>
              <SkIcon name="check" size={15} color={canSave ? '#fff' : colors.ink4} strokeWidth={2.4} />
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: canSave ? '#fff' : colors.ink4 }}>{busy ? 'Saving…' : 'Save payment'}</Text>
            </Pressable>
          </View>
        </View>
      }
    >
      <ScrollView style={{ maxHeight: 460 }} contentContainerStyle={{ gap: 14 }}>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={fieldLabel}>{isPurchase ? 'Amount paid' : 'Amount received'}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 9, height: 40, paddingHorizontal: 12 }}>
              <Text style={{ fontFamily: font.monoSemibold, fontSize: 14, color: colors.ink3 }}>{symbol}</Text>
              <TextInput value={amount} onChangeText={setAmount} keyboardType="decimal-pad" style={{ flex: 1, fontFamily: font.mono, fontSize: 14, color: colors.ink, marginLeft: 6, ...web({ outlineStyle: 'none' }) }} />
            </View>
          </View>
          <View style={{ flex: 1 }}><Text style={fieldLabel}>Payment date</Text><DateField value={date} onChange={setDate} /></View>
        </View>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={fieldLabel}>Mode</Text>
            <DropdownSelect value={mode} options={MODES} onChange={(v) => setMode(v as PaymentMode)} />
          </View>
          <View style={{ flex: 1 }}><Text style={fieldLabel}>Reference</Text><TextInput value={ref} onChangeText={setRef} placeholder="UTR / cheque no." placeholderTextColor={colors.ink4} style={inp} /></View>
        </View>

        {/* TDS section */}
        <View style={{ borderWidth: 1, borderColor: tdsOn ? colors.infoLine : colors.line, borderRadius: 8, overflow: 'hidden' }}>
          <Pressable onPress={() => setTdsOn((v) => !v)} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, backgroundColor: tdsOn ? colors.infoSoft : colors.card }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: colors.ink }}>{isPurchase ? 'You deducted TDS?' : 'Customer deducted TDS?'}</Text>
            <View style={{ width: 34, height: 18, borderRadius: 999, backgroundColor: tdsOn ? colors.info : colors.paper3, justifyContent: 'center', padding: 1 }}>
              <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: '#fff', transform: [{ translateX: tdsOn ? 16 : 0 }] }} />
            </View>
          </Pressable>
          {tdsOn && (
            <View style={{ padding: 12, gap: 12 }}>
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <View style={{ flex: 1 }}><Text style={fieldLabel}>TDS amount</Text><TextInput value={tdsAmount} onChangeText={setTdsAmount} keyboardType="decimal-pad" style={inp} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={fieldLabel}>Section</Text>
                  <DropdownSelect value={section} options={SECTIONS.map((s) => [s, s])} onChange={setSection} />
                </View>
              </View>
              <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3, lineHeight: 16 }}>
                {isPurchase
                  ? `You paid ${fmtMoney(amt, cur)} and withheld ${fmtMoney(tds, cur)} as TDS under ${section.split(' ')[0]}. The bill is settled for ${fmtMoney(gross, cur)}; ${fmtMoney(tds, cur)} is booked to TDS Payable for you to deposit.`
                  : `Customer paid ${fmtMoney(amt, cur)} and withheld ${fmtMoney(tds, cur)} as TDS under ${section.split(' ')[0]}. The invoice is settled in full for ${fmtMoney(gross, cur)}; ${fmtMoney(tds, cur)} is booked to TDS Receivable to claim against your tax liability.`}
              </Text>
            </View>
          )}
        </View>

        {/* Allocation */}
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>Allocate to open documents</Text>
            <Pressable onPress={autoAllocate} style={{ height: 28, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.accent }}>Auto-allocate</Text>
            </Pressable>
          </View>
          {!resolvedContactId ? (
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.warn, lineHeight: 17 }}>
              This {isPurchase ? 'expense has no vendor' : 'document has no customer'}, so a payment can't be recorded against it.
            </Text>
          ) : loading ? (
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>Loading open documents…</Text>
          ) : opens.length === 0 ? (
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>No open documents for this contact — payment will sit as an advance.</Text>
          ) : (
            opens.map((o) => (
              <View key={o.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, borderBottomWidth: 1, borderColor: colors.line }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontFamily: font.monoSemibold, fontSize: 12, fontWeight: '600', color: colors.ink }}>{o.number}</Text>
                  <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{o.sub} · open {fmtMoney(o.balance, cur)}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 5, height: 30, paddingHorizontal: 8, width: 130 }}>
                  <Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>{symbol}</Text>
                  <TextInput value={allocs[o.id] || ''} onChangeText={(t) => { setManualAlloc(true); setAllocs((a) => ({ ...a, [o.id]: t })); }} keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.ink4} style={{ flex: 1, textAlign: 'right', fontFamily: font.mono, fontSize: 12, color: colors.ink, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) }} />
                </View>
              </View>
            ))
          )}
        </View>
      </ScrollView>
    </ModalShell>
  );
};

const Summary: React.FC<{ label: string; value: string; color: string; font: any; colors: any }> = ({ label, value, color, font, colors }) => (
  <View>
    <Text style={{ fontFamily: font.sans, fontSize: 10, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Text>
    <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color }}>{value}</Text>
  </View>
);

export default RecordPaymentModal;
